/**
 * Portal Manta · ObrasConsolPorRodovia
 * Extraído de App.jsx v29.3 (via AST)
 */
import React, { useState, useEffect, useMemo, useCallback, Fragment, memo } from 'react';
import { SICRO_DESC } from '../../data/sicro-desc.js';
import { CONSOL_FIN } from '../../data/consol-fin.js';
import { V, S, fmtNum, sumBy, sigKey, fmtBRL, AmpliBadge, OBRAS_ROD } from '../../globals.js';
import { RodoviaContexto } from '../lotes-vis/RodoviaContexto.jsx';
import { SectionCollapse } from './SectionCollapse.jsx';
import { RetigSchematic } from '../lotes-vis/RetigSchematic.jsx';
import { THl, TH, TDl, TD } from '../../app-globals.js';

function ObrasConsolPorRodovia({lote}){
  const sig = sigKey(lote);
  const dataFin = CONSOL_FIN[sig]||{};
  const dataRod = OBRAS_ROD[sig]||{};
  const sistema = dataRod.sistema||[];
  const extLote = sistema.reduce((s,r)=>s+(r.ext||0),0)||1;
  const totSicro = Object.values(dataFin).reduce((s,arr)=>s+sumBy(arr,'v'),0);
  const [rodSel,setRodSel] = useState(sistema[0]?.rod||null);
  const [collapsed,setCollapsed] = useState(false);
  const rodInfo = sistema.find(r=>r.rod===rodSel);
  const fator = rodInfo ? rodInfo.ext/extLote : 0;
  const totRod = totSicro * fator;
  const cores = {'PAVIMENTACAO':V.or,'TERRAPLENOS E CONTENCOES':V.bl,'DRENAGEM':V.gn,
                 'OAES':V.rxn||'#7c3aed','SINALIZACAO':V.am||'#d97706',
                 'ILUMINACAO':V.ind||'#0891b2','SERVICOS PRELIMINARES':V.gr||'#475569'};
  const sumCat = (cat,rod,field)=>((dataRod[cat]||[]).filter(x=>x.rod===rod).reduce((s,x)=>s+(x[field]||0),0));
  return(<div style={{display:'flex',flex:1,overflow:'hidden'}}>
    {/* Sidebar de rodovias */}
    <div style={{width:collapsed?52:220,background:'#fff',borderRight:'1px solid '+V.b1,overflow:'auto',flexShrink:0,transition:'width .2s'}}>
      <div onClick={()=>setCollapsed(!collapsed)} style={{padding:'10px 12px',fontSize:10,fontWeight:700,color:V.gr,letterSpacing:.5,borderBottom:'1px solid '+V.b1,background:V.g0,cursor:'pointer',display:'flex',justifyContent:'space-between'}}>
        {!collapsed && <span>RODOVIAS · {sistema.length}</span>}
        <span style={{color:V.bl}}>{collapsed?'▶':'◀'}</span>
      </div>
      {sistema.map((r,i)=>{
        const ativo = r.rod===rodSel;
        const fr = r.ext/extLote;
        return(<div key={r.rod} onClick={()=>setRodSel(r.rod)} title={r.rod} style={{
          padding:collapsed?'10px 6px':'10px 12px',cursor:'pointer',
          borderLeft:'3px solid '+(ativo?V.bl:'transparent'),
          background:ativo?V.g0:'transparent',borderBottom:'1px solid '+V.g0}}>
          <div style={{display:'flex',alignItems:'center',gap:6,justifyContent:collapsed?'center':'flex-start'}}>
            <span style={{
              width:22,height:22,borderRadius:4,background:ativo?V.bl:V.g0,
              color:ativo?'#fff':V.gr,fontSize:10,fontWeight:700,
              display:'flex',alignItems:'center',justifyContent:'center'
            }}>{i+1}</span>
            {!collapsed && <span style={{fontSize:11,fontWeight:700,color:ativo?V.bl:V.br}}>{r.rod}</span>}
          </div>
          {!collapsed && <>
            <div style={{fontSize:9.5,color:V.gr,marginTop:3,marginLeft:30}}>{fmtNum(r.ext,1)} km · {(fr*100).toFixed(1)}%</div>
            <div style={{fontSize:9.5,color:V.gr,marginLeft:30}}>{fmtBRL(totSicro*fr,0)} rateado</div>
          </>}
        </div>);
      })}
    </div>
    {/* Conteúdo principal */}
    <div style={{flex:1,overflow:'auto',minWidth:0}}>
      {!rodInfo ? <div style={S.page}>Sem rodovia selecionada.</div> : (<div style={S.page}>
        <div style={S.h1}>{rodInfo.rod} · {lote.nome}</div> <AmpliBadge/>
        <div style={S.sub}>
          Extensão <b>{fmtNum(rodInfo.ext,1)} km</b> · 
          Participação no lote <b>{(fator*100).toFixed(1)}%</b> · 
          R$ rateado SICRO <b style={{color:V.br}}>{fmtBRL(totRod)}</b> · 
          R$/km <b>{fmtBRL(rodInfo.ext?totRod/rodInfo.ext:0,0)}</b>
        </div>
        {/* A · Retigráfico esquemático */}
        <RodoviaContexto rodovia={rodInfo} dataRod={dataRod} dataFin={dataFin} lote={lote}/>
        <SectionCollapse id="retig" titulo="A · Retigráfico esquemático (fora de escala)" defOpen>
          <RetigSchematic rodovia={rodInfo} obrasRod={dataRod}/>
        </SectionCollapse>
        {/* B · Quantitativo físico */}
        <SectionCollapse id="per" titulo="B · Quantitativo físico (PER + OBRAS_ROD)" defOpen>
        <div style={{...S.card,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
            <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
              <th style={THl}>Categoria</th><th style={TH}>Qtd</th><th style={TH}>Extensão (km)</th>
            </tr></thead>
            <tbody>
              {['duplicacoes','faixas','marginais','ciclovias','contornos'].map(cat=>{
                const items = (dataRod[cat]||[]).filter(x=>x.rod===rodInfo.rod);
                if(items.length===0) return null;
                const totKm = items.reduce((s,x)=>s+(x.km||0),0);
                const totN = items.reduce((s,x)=>s+(x.n||x.trechos||1),0);
                return(<tr key={cat} style={S.trLine}>
                  <td style={TDl}>{cat[0].toUpperCase()+cat.slice(1)}</td>
                  <td style={TD}>{totN}</td>
                  <td style={TD}>{fmtNum(totKm,2)}</td>
                </tr>);
              })}
              {['oaes','passarelas','diamantes','trombetas','parclos','rotatorias'].map(cat=>{
                const items = (dataRod[cat]||[]).filter(x=>x.rod===rodInfo.rod);
                if(items.length===0) return null;
                const totN = items.reduce((s,x)=>s+(x.n||1),0);
                return(<tr key={cat} style={S.trLine}>
                  <td style={TDl}>{cat[0].toUpperCase()+cat.slice(1)}</td>
                  <td style={TD}>{totN}</td>
                  <td style={TD}>—</td>
                </tr>);
              })}
            </tbody>
          </table>
        </div>
        </SectionCollapse>
        {/* C · SICRO por disciplina */}
        <SectionCollapse id="sicro" titulo="C · Serviços SICRO por disciplina (rateado)" defOpen>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:8,marginBottom:10}}>
          {Object.entries(dataFin).map(([disc,items])=>{
            const tot = sumBy(items,'v')*fator;
            const cor = cores[disc]||V.bl;
            const rkm = rodInfo.ext?tot/rodInfo.ext:0;
            return(<div key={disc} style={{...S.cardP,borderLeft:'4px solid '+cor}}>
              <div style={{fontSize:9.5,fontWeight:700,color:cor,letterSpacing:.3}}>{disc}</div>
              <div style={{fontSize:13,fontWeight:700,color:V.br,marginTop:4}}>{fmtBRL(tot)}</div>
              <div style={{fontSize:9.5,color:V.gr,marginTop:2}}>{fmtBRL(rkm,0)}/km · {items.length} itens</div>
            </div>);
          })}
        </div>
        </SectionCollapse>
        {/* D · Detalhamento de serviços (rateado) */}
        <SectionCollapse id="detalh" titulo="D · Detalhamento de serviços (rateado)" defOpen={false}>
        <div style={{...S.card,overflow:'hidden'}}>
          <div style={{maxHeight:320,overflowY:'auto'}}>
            <table style={{width:'100%',borderCollapse:'collapse',fontSize:10}}>
              <thead style={S.thRow}><tr style={{borderBottom:'2px solid '+V.b1}}>
                <th style={THl}>SICRO</th>
                <th style={THl}>Descrição</th>
                <th style={THl}>Disc.</th>
                <th style={TH}>Und</th>
                <th style={TH}>Qtd rod</th>
                <th style={TH}>P.U.s/BDI</th>
                <th style={TH}>BDI</th>
                <th style={TH}>P.U.c/BDI</th>
                <th style={TH}>R$ rod</th>
              </tr></thead>
              <tbody>
                {Object.entries(dataFin).flatMap(([d,items])=>items.map(it=>({...it,_d:d}))).map((r,i)=>(
                  <tr key={i} style={S.trLine}>
                    <td style={{...TDl,fontFamily:'monospace',fontSize:9.5}}>{r.c}</td>
                    <td style={{...TDl,maxWidth:280,whiteSpace:'normal'}}>{SICRO_DESC[r.c]||''}</td>
                    <td style={{...TDl,fontSize:9.5,color:V.gr}}>{r._d}</td>
                    <td style={TD}>{r.u}</td>
                    <td style={{...TD,color:V.bl,fontWeight:600}}>{fmtNum(r.q*fator)}</td>
                    <td style={TD}>{fmtNum(r.p)}</td>
                    <td style={TD}>{(r.b*100).toFixed(2)}%</td>
                    <td style={TD}>{fmtNum(r.p*(1+r.b))}</td>
                    <td style={{...TD,fontWeight:700,color:V.bl}}>{fmtBRL(r.v*fator,0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        </SectionCollapse>
        {/* E · Composições unitárias */}
        <SectionCollapse id="comp" titulo="E · Composições unitárias (códigos SICRO únicos)" defOpen={false}>
        <div style={{fontSize:11,color:V.gr,marginBottom:6}}>
          Os códigos SICRO usados nesta rodovia são os mesmos de todo o lote (rateio por extensão). Use a página dedicada de composições para drilldown completo.
        </div>
        </SectionCollapse>
      </div>)}
    </div>
  </div>);
}

export { ObrasConsolPorRodovia };
export default ObrasConsolPorRodovia;
